(function () {
  let cachedProductVariants = null;
  let hasForcedPlaceholder = false;
  // Survives Alpine innerHTML swaps that can wipe element dataset mid-update.
  const calendarMemoryByPicker = new Map();
  // Bumped when calendar selection becomes incomplete so stale section HTML is ignored.
  let productInfoSyncGeneration = 0;

  const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTH_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  function dateFromKey(dateKey) {
    if (!dateKey) return null;
    const [y, m, d] = dateKey.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  function formatTimeButtonLabel(dateKey, timeLabel) {
    const date = dateFromKey(dateKey);
    if (!date || !timeLabel) return timeLabel || '';
    return `${WEEKDAY_LONG[date.getDay()]} ${timeLabel}`;
  }

  function formatSessionSummary(dateKey, timeLabel) {
    const date = dateFromKey(dateKey);
    if (!date) return '';
    const base = `${WEEKDAY_LONG[date.getDay()]} ${date.getDate()} ${MONTH_LONG[date.getMonth()]} ${date.getFullYear()}`;
    return timeLabel ? `${base} · ${timeLabel}` : base;
  }

  function cancelInFlightProductInfo() {
    productInfoSyncGeneration += 1;
    try {
      if (typeof Alpine !== 'undefined') {
        Alpine.store('xHelper')?.cancelRequest?.('eurus:product_info_request');
      }
    } catch (error) {
      // Alpine may not be ready yet.
    }
    return productInfoSyncGeneration;
  }

  function beginProductInfoSync() {
    return productInfoSyncGeneration;
  }

  function isStaleProductInfoSync(generation) {
    return generation != null && generation !== productInfoSyncGeneration;
  }

  function reassertPersistedSessionRadios(picker) {
    if (!picker) return;
    const { dateFieldset, timeFieldset } = getSessionFieldsets(picker);
    const persisted = readPersistedCalendarState(picker);

    if (persisted.dateLabel) {
      const dateInput = findRadioByValue(dateFieldset, persisted.dateLabel);
      if (dateInput && !isRadioUnavailable(dateInput)) {
        setRadioCheckedQuiet(picker, dateInput);
      }
    } else {
      dateFieldset?.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });
    }

    if (persisted.timeLabel) {
      const timeInput = findRadioByValue(timeFieldset, persisted.timeLabel);
      if (timeInput && !isRadioUnavailable(timeInput)) {
        setRadioCheckedQuiet(picker, timeInput);
      }
    } else {
      timeFieldset?.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });
    }

    const select = getSessionSelect(picker);
    if (select) {
      if (persisted.dateLabel && persisted.timeLabel) {
        syncSessionSelectFromRadios(picker);
      } else {
        Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
        select.value = '';
        const placeholder = select.querySelector('option[value=""]');
        if (placeholder) placeholder.setAttribute('selected', 'selected');
        setSessionSelectedState(picker, false);
      }
    }
  }

  function escapeSelector(value) {
    if (window.CSS?.escape) return CSS.escape(value);
    return value.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  function getProductVariants() {
    if (cachedProductVariants) return cachedProductVariants;

    const script = document.querySelector('[data-product-variants-json]');
    if (!script) return [];

    try {
      cachedProductVariants = JSON.parse(script.textContent);
    } catch {
      cachedProductVariants = [];
    }

    return cachedProductVariants;
  }

  function getVariantSelectData(picker) {
    const variantSelect = picker.closest('.variant-selects');
    if (!variantSelect) return null;

    if (typeof Alpine !== 'undefined' && Alpine.$data) {
      try {
        return Alpine.$data(variantSelect);
      } catch (error) {
        // Fall through to Alpine internal stack.
      }
    }

    return variantSelect._x_dataStack?.[0] || null;
  }

  function getSessionSelect(picker) {
    return picker.querySelector('[data-workshop-session-select]');
  }

  function getSessionFieldsets(picker) {
    const sessionPicker = picker.querySelector('[data-workshop-session-picker]');
    const fieldsets = sessionPicker?.querySelectorAll('fieldset[data-option-index]') || [];
    return { dateFieldset: fieldsets[0] || null, timeFieldset: fieldsets[1] || null };
  }

  function findRadioByValue(fieldset, value) {
    if (!fieldset) return null;
    return Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((input) => input.value === value) || null;
  }

  function isRadioUnavailable(input) {
    return !input || input.disabled || input.classList.contains('disabled') || input.dataset.available === 'false';
  }

  function selectRadioInput(picker, input) {
    if (isRadioUnavailable(input)) return false;

    const label = picker.querySelector(`label[for="${escapeSelector(input.id)}"]`);
    if (label) {
      label.click();
      return input.checked;
    }

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function countSelectedOptions(picker) {
    return picker.querySelectorAll('fieldset[data-option-index] input[type="radio"]:checked:not(.disabled)').length;
  }

  function countTotalOptions(picker) {
    return picker.querySelectorAll('fieldset[data-option-index]').length;
  }

  function getCurrentSelections(picker) {
    const selections = {};

    picker.querySelectorAll('fieldset[data-option-index]').forEach((fieldset) => {
      const idx = parseInt(fieldset.dataset.optionIndex, 10);
      const checked = fieldset.querySelector('input[type="radio"]:checked:not(.disabled)');
      if (checked) selections[idx] = checked.value;
    });

    return selections;
  }

  function getVariantFromInput(picker, input) {
    const script = picker.querySelector(`script[type="application/json"][data-resource="${input.id}"]`);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  function findVariantForSelections(picker, lampInput, pendingSelections) {
    const selections = pendingSelections || getCurrentSelections(picker);
    const selectedIndices = Object.keys(selections).map((idx) => parseInt(idx, 10));
    const optionCount = countTotalOptions(picker);
    const variants = getProductVariants();

    if (selectedIndices.length === optionCount) {
      const exact = variants.find((variant) =>
        variant.options.every((optionValue, idx) => selections[idx] === optionValue)
      );
      if (exact?.featured_media_id) return exact;
    }

    if (selectedIndices.length > 0) {
      const partial = variants.find((variant) => {
        if (!variant.featured_media_id) return false;
        return selectedIndices.every((idx) => variant.options[idx] === selections[idx]);
      });
      if (partial) return partial;
    }

    if (lampInput) {
      const fallback = getVariantFromInput(picker, lampInput);
      if (fallback?.featured_media?.id) {
        return { featured_media_id: fallback.featured_media.id };
      }
    }

    return null;
  }

  function triggerVariantUpdate(picker, input) {
    if (!input) return;

    const alpineData = getVariantSelectData(picker);
    if (alpineData?.updateVariantSelector) {
      alpineData.updateVariantSelector.call(alpineData, input.id, input.dataset.productUrl || '');
      return;
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function goToVariantMedia(picker, variant) {
    const mediaId = variant?.featured_media?.id ?? variant?.featured_media_id;
    if (!mediaId) return;

    const sectionId = picker.id.replace('variant-update-', '');
    const slideVariant = document.getElementsByClassName(`${mediaId}-${sectionId}`);
    const index = parseInt(slideVariant[0]?.getAttribute('index'), 10);
    const splideEl = document.getElementById(`x-product-${sectionId}`);
    const activeEL = document.getElementById(`postion-image-${sectionId}-${mediaId}`);

    const goToSlide = () => {
      if (splideEl?.splide && !Number.isNaN(index)) {
        splideEl.splide.go(index);
      }
    };

    goToSlide();
    if (splideEl && !splideEl.splide) {
      document.addEventListener(`eurus:media-gallery-ready:${sectionId}`, goToSlide, { once: true });
    }

    const stackedEL = document.getElementById(`stacked-${sectionId}`);
    if (stackedEL && activeEL) {
      stackedEL.prepend(activeEL);
    }
  }

  function updateLampMedia(picker, lampInput, pendingSelections) {
    const variant = findVariantForSelections(picker, lampInput, pendingSelections);
    if (variant) goToVariantMedia(picker, variant);
  }

  function handleLampChange(input) {
    const picker = input.closest('.workshop-variant-picker');
    if (!picker) return;

    // Alpine already listens for lamp radio change and calls updateVariantSelector.
    // Only preview media here to avoid a second section fetch.
    updateLampMedia(picker, input);

    // Keep calendar selection; only refresh availability for the new lamp.
    if (typeof picker.__wsCalRefresh === 'function') {
      picker.__wsCalRefresh();
    }

    updateSessionNudges(picker);
  }

  function getSessionCompleteness(picker) {
    const { dateFieldset, timeFieldset } = getSessionFieldsets(picker);
    const persisted = readPersistedCalendarState(picker);
    const dateChecked = dateFieldset?.querySelector('input[type="radio"]:checked:not(.disabled)');
    const timeChecked = timeFieldset?.querySelector('input[type="radio"]:checked:not(.disabled)');
    const hasDate = !!(dateChecked || persisted.dateLabel || persisted.dateKey);
    const hasTime = !!(timeChecked || persisted.timeLabel);
    const lampChecked = picker.querySelector(
      '[data-workshop-lamp-picker] input[type="radio"]:checked:not(.disabled)'
    );
    return {
      hasDate,
      hasTime,
      hasLamp: !!lampChecked,
      complete: !!(hasDate && hasTime && lampChecked),
    };
  }

  function setNudgeVisible(el, visible) {
    if (!el) return;
    if (visible) {
      el.hidden = false;
      el.removeAttribute('hidden');
      el.classList.add('is-visible');
    } else {
      el.hidden = true;
      el.setAttribute('hidden', '');
      el.classList.remove('is-visible', 'is-attention');
    }
  }

  function updateSessionNudges(picker) {
    if (!picker || picker.dataset.workshopCalendarMode !== 'true') return;

    const dateNudge = picker.querySelector('[data-cal-date-nudge]');
    const timeNudge = picker.querySelector('[data-cal-time-nudge]');
    const { hasDate, hasTime, hasLamp } = getSessionCompleteness(picker);

    const needDate = hasLamp && !hasDate;
    const needTime = hasLamp && hasDate && !hasTime;

    setNudgeVisible(dateNudge, needDate);
    setNudgeVisible(timeNudge, needTime);
    syncWorkshopAtcClickability(picker);
  }

  function pulseNudge(el) {
    if (!el) return;
    el.classList.remove('is-attention');
    // Force reflow so animation restarts.
    void el.offsetWidth;
    el.classList.add('is-attention');
    window.setTimeout(() => el.classList.remove('is-attention'), 1600);
  }

  function focusIncompleteSession(picker) {
    if (!picker) return;
    updateSessionNudges(picker);
    const { hasDate, hasTime, hasLamp } = getSessionCompleteness(picker);
    if (!hasLamp || (hasDate && hasTime)) return;

    const dateStep = picker.querySelector('[data-cal-date-step]');
    const timeStep = picker.querySelector('[data-cal-time-step]');
    const dateNudge = picker.querySelector('[data-cal-date-nudge]');
    const timeNudge = picker.querySelector('[data-cal-time-nudge]');

    const target = !hasDate ? dateStep : timeStep;
    const nudge = !hasDate ? dateNudge : timeNudge;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    pulseNudge(nudge);
  }

  function formatWorkshopMoney(cents) {
    try {
      if (typeof Alpine !== 'undefined' && Alpine.store('xHelper')?.formatMoney) {
        const format = window.theme?.moneyFormat || '{{amount}}';
        return Alpine.store('xHelper').formatMoney(cents, format);
      }
    } catch (error) {
      // fall through
    }
    const value = Number(cents);
    if (Number.isNaN(value)) return '';
    return `£${(value / 100).toFixed(2)}`;
  }

  function setWorkshopAtcReadyLabel(addButton, variant) {
    const textEl = addButton?.querySelector('.x-atc-text');
    if (!textEl) return;
    const price = variant?.price != null ? formatWorkshopMoney(variant.price) : '';
    textEl.textContent = price ? `Add to cart – ${price}` : 'Add to cart';
  }

  function activateWorkshopAtc(picker, form, addButton) {
    const selections = getCurrentSelections(picker);
    const variant = findExactVariant(selections);
    if (!variant || variant.available === false) return false;

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = String(variant.id);

    addButton.removeAttribute('disabled');
    addButton.removeAttribute('aria-disabled');
    addButton.classList.remove('is-workshop-incomplete');
    addButton.style.removeProperty('display');
    addButton.style.removeProperty('align-items');
    addButton.style.removeProperty('justify-content');
    addButton.style.removeProperty('text-align');
    addButton.style.removeProperty('opacity');
    addButton.style.removeProperty('pointer-events');
    setWorkshopAtcReadyLabel(addButton, variant);

    const alpineData = getVariantSelectData(picker);
    if (alpineData) {
      // Merge slim JSON fields into Alpine's richer currentVariant (media/inventory).
      alpineData.currentVariant = {
        ...(alpineData.currentVariant || {}),
        ...variant,
        id: variant.id,
        available: variant.available !== false,
        price: variant.price,
      };
    }

    return true;
  }

  function syncWorkshopAtcClickability(picker) {
    if (!picker || picker.dataset.workshopCalendarMode !== 'true') return;

    const sectionId = picker.id.replace('variant-update-', '');
    const { hasDate, hasTime, hasLamp, complete } = getSessionCompleteness(picker);
    const incomplete = hasLamp && (!hasDate || !hasTime);
    const sessions = collectSessions(picker);
    const hasBookableSession = sessions.some((session) => session.available);
    const promptLabel = !hasDate ? 'Select a date & time' : 'Select a time';

    document.querySelectorAll(`#product-form-${sectionId}, #product-form-sticky-${sectionId}`).forEach((form) => {
      const addButton = form.querySelector('.add_to_cart_button');
      if (!addButton) return;

      if (complete) {
        // Don't wait for Alpine section fetch — enable ATC as soon as date+time+lamp match.
        if (!activateWorkshopAtc(picker, form, addButton)) {
          // Complete UI state but no matching available variant — keep ATC blocked.
          addButton.classList.remove('is-workshop-incomplete');
          addButton.removeAttribute('aria-disabled');
          addButton.setAttribute('disabled', 'disabled');
          addButton.style.removeProperty('display');
          addButton.style.removeProperty('align-items');
          addButton.style.removeProperty('justify-content');
          addButton.style.removeProperty('text-align');
          addButton.style.removeProperty('opacity');
          addButton.style.removeProperty('pointer-events');
          setWorkshopAtcPromptLabel(addButton, 'Unavailable');
          const variantInput = form.querySelector('input[name="id"]');
          if (variantInput) variantInput.value = '';
        }
        return;
      }

      if (incomplete) {
        // Keep clickable/visible so we can scroll/pulse; block real add via guard + empty id.
        addButton.removeAttribute('disabled');
        addButton.setAttribute('aria-disabled', 'true');
        addButton.classList.add('is-workshop-incomplete');
        addButton.style.setProperty('display', 'flex', 'important');
        addButton.style.setProperty('align-items', 'center', 'important');
        addButton.style.setProperty('justify-content', 'center', 'important');
        addButton.style.setProperty('text-align', 'center', 'important');
        addButton.style.setProperty('opacity', '1', 'important');
        addButton.style.setProperty('pointer-events', 'auto', 'important');
        setWorkshopAtcPromptLabel(addButton, promptLabel);
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = '';
      } else if (hasBookableSession) {
        // Avoid false "Sold out" after we clear the default variant on load / draft preview.
        addButton.classList.remove('is-workshop-incomplete');
        addButton.removeAttribute('aria-disabled');
        addButton.setAttribute('disabled', 'disabled');
        addButton.style.removeProperty('display');
        addButton.style.removeProperty('align-items');
        addButton.style.removeProperty('justify-content');
        addButton.style.removeProperty('text-align');
        addButton.style.removeProperty('opacity');
        addButton.style.removeProperty('pointer-events');
        setWorkshopAtcPromptLabel(addButton, 'Select a date & time');
        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = '';
      }
    });
  }

  function bindAtcGuards(picker) {
    if (!picker || picker.dataset.workshopCalendarMode !== 'true') return;
    if (window.__wsAtcGuardBound) return;
    window.__wsAtcGuardBound = true;

    const blockIncompleteAdd = (event, form) => {
      if (!form?.querySelector?.('.add_to_cart_button.is-workshop-incomplete')) return false;

      const formId = form.getAttribute('id') || '';
      const match = formId.match(/^product-form-(?:sticky-)?(.+)$/);
      if (!match) return false;

      const activePicker = document.getElementById(`variant-update-${match[1]}`);
      if (!activePicker || activePicker.dataset.workshopCalendarMode !== 'true') return false;

      event.preventDefault();
      event.stopImmediatePropagation();
      focusIncompleteSession(activePicker);
      return true;
    };

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.('.add_to_cart_button.is-workshop-incomplete');
        if (!button) return;
        blockIncompleteAdd(event, button.closest('form'));
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        blockIncompleteAdd(event, form);
      },
      true
    );
  }

  function getSelectedSessionOption(select) {
    if (!select || select.selectedIndex < 0) return null;
    const option = select.options[select.selectedIndex];
    if (!option || !option.dataset.dateValue) return null;
    return option;
  }

  function getSessionControl(picker) {
    return picker.querySelector('[data-workshop-session-control]');
  }

  function setSessionSelectedState(picker, isSelected) {
    const control = getSessionControl(picker);
    if (control) control.classList.toggle('is-selected', isSelected);
  }

  function getCalendarHost(picker) {
    return picker.closest('.variant-selects') || picker;
  }

  function getCalendarMemoryKey(picker) {
    return picker?.id || getCalendarHost(picker)?.id || 'workshop-calendar';
  }

  function persistCalendarState(picker, state) {
    if (!picker || !state) return;
    const host = getCalendarHost(picker);
    const key = getCalendarMemoryKey(picker);
    const prev = calendarMemoryByPicker.get(key) || {
      dateKey: host?.dataset.wsCalDateKey || '',
      dateLabel: host?.dataset.wsCalDateLabel || '',
      timeLabel: host?.dataset.wsCalTimeLabel || '',
    };
    const next = { ...prev };
    if (Object.prototype.hasOwnProperty.call(state, 'dateKey')) next.dateKey = state.dateKey || '';
    if (Object.prototype.hasOwnProperty.call(state, 'dateLabel')) next.dateLabel = state.dateLabel || '';
    if (Object.prototype.hasOwnProperty.call(state, 'timeLabel')) next.timeLabel = state.timeLabel || '';
    calendarMemoryByPicker.set(key, next);
    if (host) {
      host.dataset.wsCalDateKey = next.dateKey;
      host.dataset.wsCalDateLabel = next.dateLabel;
      host.dataset.wsCalTimeLabel = next.timeLabel;
    }
  }

  function readPersistedCalendarState(picker) {
    const host = getCalendarHost(picker);
    const mem = calendarMemoryByPicker.get(getCalendarMemoryKey(picker)) || {};
    return {
      dateKey: mem.dateKey || host?.dataset.wsCalDateKey || '',
      dateLabel: mem.dateLabel || host?.dataset.wsCalDateLabel || '',
      timeLabel: mem.timeLabel || host?.dataset.wsCalTimeLabel || '',
    };
  }

  function setRadioCheckedQuiet(picker, input) {
    if (isRadioUnavailable(input)) return false;
    picker.querySelectorAll(`input[type="radio"][name="${escapeSelector(input.name)}"]`).forEach((radio) => {
      radio.checked = false;
    });
    input.checked = true;
    return true;
  }

  function findExactVariant(selections) {
    const variants = getProductVariants();
    const keys = Object.keys(selections);
    if (!keys.length) return null;
    return (
      variants.find((variant) =>
        variant.options.every((optionValue, idx) => selections[idx] === optionValue)
      ) || null
    );
  }

  function isComboAvailable(picker, dateValue, timeValue, lampValue) {
    const selections = getCurrentSelections(picker);
    const { dateFieldset, timeFieldset } = getSessionFieldsets(picker);
    const lampFieldset = picker.querySelector('[data-workshop-lamp-picker]');
    if (dateFieldset) selections[parseInt(dateFieldset.dataset.optionIndex, 10)] = dateValue;
    if (timeFieldset) selections[parseInt(timeFieldset.dataset.optionIndex, 10)] = timeValue;
    if (lampValue != null && lampFieldset) {
      selections[parseInt(lampFieldset.dataset.optionIndex, 10)] = lampValue;
    }
    const exact = findExactVariant(selections);
    return !!(exact && exact.available !== false);
  }

  function applySessionSelection(picker, dateValue, timeValue, options = {}) {
    const { dateFieldset, timeFieldset } = getSessionFieldsets(picker);
    const dateInput = findRadioByValue(dateFieldset, dateValue);
    const timeInput = findRadioByValue(timeFieldset, timeValue);
    if (isRadioUnavailable(dateInput) || isRadioUnavailable(timeInput)) return false;

    // Quiet date/time sets avoid Alpine double-fetch; one explicit update at the end.
    setRadioCheckedQuiet(picker, dateInput);
    setRadioCheckedQuiet(picker, timeInput);
    setSessionSelectedState(picker, true);

    const select = getSessionSelect(picker);
    if (select) {
      const match = Array.from(select.options).find(
        (option) => option.dataset.dateValue === dateValue && option.dataset.timeValue === timeValue
      );
      if (match && !match.disabled) {
        Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
        match.setAttribute('selected', 'selected');
        select.value = match.value;
      }
    }

    let dateKey = options.dateKey || '';
    if (!dateKey && window.WorkshopDateUtils?.parseWorkshopLabel) {
      const parsed = window.WorkshopDateUtils.parseWorkshopLabel(dateValue, new Date().getFullYear());
      if (parsed) dateKey = toDateKey(parsed);
    }
    persistCalendarState(picker, {
      dateKey,
      dateLabel: dateValue,
      timeLabel: timeValue,
    });

    if (options.quiet === true) return true;

    // Activate ATC immediately — don't wait for Alpine's section fetch round-trip.
    updateSessionNudges(picker);

    if (countSelectedOptions(picker) >= countTotalOptions(picker)) {
      triggerVariantUpdate(picker, timeInput);
    }

    return true;
  }

  function syncSessionSelectFromRadios(picker) {
    const select = getSessionSelect(picker);
    if (!select) return;

    const { dateFieldset, timeFieldset } = getSessionFieldsets(picker);
    const dateChecked = dateFieldset?.querySelector('input[type="radio"]:checked');
    const timeChecked = timeFieldset?.querySelector('input[type="radio"]:checked');

    if (!dateChecked || !timeChecked) {
      setSessionSelectedState(picker, false);
      return;
    }

    const match = Array.from(select.options).find(
      (option) => option.dataset.dateValue === dateChecked.value && option.dataset.timeValue === timeChecked.value
    );

    if (match && !match.disabled) {
      Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
      match.setAttribute('selected', 'selected');
      select.value = match.value;
      setSessionSelectedState(picker, true);
    } else {
      setSessionSelectedState(picker, false);
    }
  }

  function handleSessionChange(select) {
    const picker = select.closest('.workshop-variant-picker');
    if (!picker) return;

    const option = getSelectedSessionOption(select);
    if (!option || option.disabled) {
      setSessionSelectedState(picker, false);
      return;
    }

    applySessionSelection(picker, option.dataset.dateValue, option.dataset.timeValue);
  }

  function sortSessionOptions(picker) {
    const select = getSessionSelect(picker);
    if (!select) return;

    const placeholder = select.querySelector('option[value=""]');
    const options = Array.from(select.querySelectorAll('[data-workshop-session-option]'));
    options.sort((a, b) => {
      const dateA = new Date(a.dataset.sortDate || a.dataset.dateValue);
      const dateB = new Date(b.dataset.sortDate || b.dataset.dateValue);
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
      return dateA - dateB;
    });

    options.forEach((option) => select.appendChild(option));
    if (placeholder) select.insertBefore(placeholder, select.firstChild);
  }

  function disableAddToCart(picker) {
    const sectionId = picker.id.replace('variant-update-', '');
    document.querySelectorAll(`#product-form-${sectionId}, #product-form-sticky-${sectionId}`).forEach((form) => {
      const addButton = form.querySelector('.add_to_cart_button');
      if (addButton) addButton.setAttribute('disabled', 'disabled');
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = '';
    });

    const alpineData = getVariantSelectData(picker);
    if (alpineData) alpineData.currentVariant = null;

    syncWorkshopAtcClickability(picker);
  }

  function clearDefaultSessionSelection(picker) {
    const select = getSessionSelect(picker);
    if (!select) return;

    const sessionPicker = picker.querySelector('[data-workshop-session-picker]');
    if (!sessionPicker) return;

    sessionPicker.querySelectorAll('fieldset[data-option-index] input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
    });

    Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
    select.value = '';
    const placeholder = select.querySelector('option[value=""]');
    if (placeholder) placeholder.setAttribute('selected', 'selected');

    setSessionSelectedState(picker, false);
    disableAddToCart(picker);
  }

  function parseSessionDate(option) {
    if (option.dataset.sortDate) {
      const iso = new Date(`${option.dataset.sortDate}T12:00:00`);
      if (!isNaN(iso.getTime())) return iso;
    }

    const year = new Date().getFullYear();
    if (window.WorkshopDateUtils?.parseWorkshopLabel) {
      return window.WorkshopDateUtils.parseWorkshopLabel(option.dataset.dateValue, year);
    }

    return null;
  }

  function toDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function collectSessions(picker) {
    const select = getSessionSelect(picker);
    if (!select) return [];

    return Array.from(select.querySelectorAll('[data-workshop-session-option]'))
      .map((option) => {
        const date = parseSessionDate(option);
        if (!date) return null;
        return {
          dateKey: toDateKey(date),
          dateLabel: option.dataset.dateValue,
          timeLabel: option.dataset.timeValue,
          available: !option.disabled,
          date,
        };
      })
      .filter(Boolean);
  }

  function getAvailableMonthKeys(sessions) {
    const available = sessions.filter((session) => session.available);
    // Draft / sold-out previews often have zero available sessions — still build months
    // from parsed options so the calendar shell isn't left blank.
    const source = available.length ? available : sessions;
    const keys = new Set();
    source.forEach((session) => {
      keys.add(`${session.date.getFullYear()}-${session.date.getMonth()}`);
    });
    return Array.from(keys)
      .map((key) => {
        const [year, month] = key.split('-').map(Number);
        return { year, month, key };
      })
      .sort((a, b) => a.year - b.year || a.month - b.month);
  }

  function isCalendarRenderBroken(root) {
    if (!root || root.hidden) return false;
    // Intentional empty state after init — not broken.
    if (root.dataset.calendarInit === 'true' && root.querySelector('.workshop-calendar__empty')) return false;
    const grid = root.querySelector('[data-cal-grid]');
    const monthLabel = root.querySelector('[data-cal-month-label]');
    if (!grid || !monthLabel) return true;
    if (grid.classList.contains('is-skeleton')) return true;
    return !monthLabel.textContent?.trim() || grid.childElementCount === 0;
  }

  function renderCalendarSkeleton(root) {
    if (!root) return;
    const grid = root.querySelector('[data-cal-grid]');
    const monthLabel = root.querySelector('[data-cal-month-label]');
    const timesWrap = root.querySelector('[data-cal-times]');
    const timeList = root.querySelector('[data-cal-time-list]');

    if (monthLabel) {
      monthLabel.textContent = '';
      monthLabel.classList.add('is-skeleton');
      monthLabel.setAttribute('aria-hidden', 'true');
    }
    if (grid) {
      grid.classList.add('is-skeleton');
      grid.setAttribute('aria-busy', 'true');
      grid.innerHTML = Array.from({ length: 35 }, () => '<span class="workshop-calendar__skel-day" aria-hidden="true"></span>').join('');
    }
    if (timesWrap) timesWrap.classList.add('is-skeleton', 'is-empty');
    if (timeList) {
      timeList.innerHTML =
        '<span class="workshop-calendar__skel-time" aria-hidden="true"></span><span class="workshop-calendar__skel-time" aria-hidden="true"></span>';
    }
  }

  function clearCalendarSkeleton(root) {
    if (!root) return;
    const grid = root.querySelector('[data-cal-grid]');
    const monthLabel = root.querySelector('[data-cal-month-label]');
    const timesWrap = root.querySelector('[data-cal-times]');
    grid?.classList.remove('is-skeleton');
    grid?.removeAttribute('aria-busy');
    monthLabel?.classList.remove('is-skeleton');
    monthLabel?.removeAttribute('aria-hidden');
    timesWrap?.classList.remove('is-skeleton');
  }

  function setWorkshopAtcPromptLabel(addButton, label) {
    const textEl = addButton?.querySelector('.x-atc-text');
    if (!textEl) return;
    textEl.textContent = label;
  }

  function initCalendar(picker) {
    const root = picker.querySelector('[data-workshop-calendar]');
    if (!root) return;

    // Allow recovery when a prior init aborted or Alpine remount left an empty shell.
    if (root.dataset.calendarInit === 'true' && !isCalendarRenderBroken(root)) return;

    if (isCalendarRenderBroken(root) || root.dataset.calendarInit !== 'true') {
      renderCalendarSkeleton(root);
    }

    const replaceNode = (el) => {
      if (!el?.parentNode) return el;
      const clone = el.cloneNode(true);
      el.parentNode.replaceChild(clone, el);
      return clone;
    };

    let grid = root.querySelector('[data-cal-grid]');
    let monthLabel = root.querySelector('[data-cal-month-label]');
    let prevBtn = root.querySelector('[data-cal-prev]');
    let nextBtn = root.querySelector('[data-cal-next]');
    let prevLabel = root.querySelector('[data-cal-prev-label]');
    let nextLabel = root.querySelector('[data-cal-next-label]');
    const timesWrap = root.querySelector('[data-cal-times]');
    const timeList = root.querySelector('[data-cal-time-list]');
    const sessionSummary = picker.querySelector('[data-cal-session-summary]');
    const sessionSummaryText = picker.querySelector('[data-cal-session-summary-text]');

    // Drop stale nav listeners from a previous failed/partial init on the same root.
    if (root.dataset.calendarListeners === 'true' || root.dataset.calendarInit === 'pending') {
      prevBtn = replaceNode(prevBtn);
      nextBtn = replaceNode(nextBtn);
      prevLabel = prevBtn?.querySelector('[data-cal-prev-label]') || root.querySelector('[data-cal-prev-label]');
      nextLabel = nextBtn?.querySelector('[data-cal-next-label]') || root.querySelector('[data-cal-next-label]');
      root.dataset.calendarListeners = '';
    }

    root.dataset.calendarInit = 'pending';

    if (!grid || !monthLabel || !prevBtn || !nextBtn || !timesWrap || !timeList) {
      root.dataset.calendarInit = '';
      return;
    }

    const sessions = collectSessions(picker);
    const months = getAvailableMonthKeys(sessions);
    if (!months.length) {
      root.hidden = false;
      clearCalendarSkeleton(root);
      monthLabel.textContent = '';
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      grid.innerHTML =
        '<p class="workshop-calendar__empty">No upcoming workshop dates right now.</p>';
      timesWrap.classList.add('is-empty');
      timeList.innerHTML =
        '<p class="workshop-calendar__times-placeholder">Dates unavailable</p>';
      root.dataset.calendarInit = 'true';
      return;
    }

    root.hidden = false;

    let monthIndex = 0;
    let selectedDateKey = null;
    let selectedTime = null;

    const sessionsByDate = sessions.reduce((acc, session) => {
      if (!acc[session.dateKey]) acc[session.dateKey] = [];
      acc[session.dateKey].push(session);
      return acc;
    }, {});

    function monthName(year, month) {
      return `${MONTH_LONG[month]} ${year}`;
    }

    function shortMonthName(year, month) {
      return MONTH_LONG[month].slice(0, 3);
    }

    function restoreSelectionFromRadios() {
      const { dateFieldset, timeFieldset } = getSessionFieldsets(picker);
      const persisted = readPersistedCalendarState(picker);
      let dateChecked = dateFieldset?.querySelector('input[type="radio"]:checked');
      let timeChecked = timeFieldset?.querySelector('input[type="radio"]:checked');

      selectedDateKey = null;
      selectedTime = null;

      // Prefer in-memory/persisted session over Alpine remount radios — remount HTML
      // often briefly reverts to Shopify defaults and was wiping the calendar UI.
      if (persisted.dateLabel) {
        const persistedDate = findRadioByValue(dateFieldset, persisted.dateLabel);
        if (persistedDate && !isRadioUnavailable(persistedDate)) {
          dateChecked = persistedDate;
          setRadioCheckedQuiet(picker, dateChecked);
        }
      }
      if (persisted.timeLabel) {
        const persistedTime = findRadioByValue(timeFieldset, persisted.timeLabel);
        if (persistedTime && !isRadioUnavailable(persistedTime)) {
          timeChecked = persistedTime;
          setRadioCheckedQuiet(picker, timeChecked);
        }
      }

      if (dateChecked) {
        const match = sessions.find((session) => session.dateLabel === dateChecked.value);
        if (match) {
          selectedDateKey = match.dateKey;
          const idx = months.findIndex(
            (month) => month.year === match.date.getFullYear() && month.month === match.date.getMonth()
          );
          if (idx >= 0) monthIndex = idx;
        }
      } else if (persisted.dateKey && sessionsByDate[persisted.dateKey]) {
        selectedDateKey = persisted.dateKey;
        const match = sessionsByDate[persisted.dateKey][0];
        if (match) {
          const idx = months.findIndex(
            (month) => month.year === match.date.getFullYear() && month.month === match.date.getMonth()
          );
          if (idx >= 0) monthIndex = idx;
        }
      }

      if (timeChecked) {
        selectedTime = timeChecked.value;
      } else if (persisted.timeLabel) {
        selectedTime = persisted.timeLabel;
      }

      if (selectedDateKey && selectedTime) {
        const session = (sessionsByDate[selectedDateKey] || []).find(
          (item) => item.timeLabel === selectedTime && item.available
        );
        const lampChecked = picker.querySelector('[data-workshop-lamp-picker] input[type="radio"]:checked:not(.disabled)');
        const comboOk =
          session &&
          (!lampChecked || isComboAvailable(picker, session.dateLabel, session.timeLabel, lampChecked.value));

        if (comboOk) {
          applySessionSelection(picker, session.dateLabel, session.timeLabel, {
            quiet: true,
            dateKey: selectedDateKey,
          });
        } else if (session) {
          // Lamp changed to an incompatible combo — keep date, clear time.
          selectedTime = null;
          const { dateFieldset: df, timeFieldset: tf } = getSessionFieldsets(picker);
          const dateInput = findRadioByValue(df, session.dateLabel);
          tf?.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
            input.checked = false;
          });
          if (dateInput) setRadioCheckedQuiet(picker, dateInput);
          persistCalendarState(picker, {
            dateKey: selectedDateKey,
            dateLabel: session.dateLabel,
            timeLabel: '',
          });
          setSessionSelectedState(picker, false);
          disableAddToCart(picker);
        }
      } else if (selectedDateKey) {
        const session = (sessionsByDate[selectedDateKey] || []).find((item) => item.available);
        if (session) {
          const { dateFieldset: df } = getSessionFieldsets(picker);
          const dateInput = findRadioByValue(df, session.dateLabel);
          if (dateInput) setRadioCheckedQuiet(picker, dateInput);
          persistCalendarState(picker, {
            dateKey: selectedDateKey,
            dateLabel: session.dateLabel,
            timeLabel: '',
          });
        }
      }
    }

    function updateSessionSummary() {
      if (!sessionSummary || !sessionSummaryText) return;
      if (selectedDateKey && selectedTime) {
        sessionSummaryText.textContent = formatSessionSummary(selectedDateKey, selectedTime);
        sessionSummary.hidden = false;
        sessionSummary.removeAttribute('hidden');
        sessionSummary.classList.add('is-visible');
      } else {
        sessionSummaryText.textContent = '';
        sessionSummary.hidden = true;
        sessionSummary.setAttribute('hidden', '');
        sessionSummary.classList.remove('is-visible');
      }
    }

    function clearTimeSelection() {
      cancelInFlightProductInfo();

      const { timeFieldset } = getSessionFieldsets(picker);
      timeFieldset?.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });

      const select = getSessionSelect(picker);
      if (select) {
        Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
        select.value = '';
        const placeholder = select.querySelector('option[value=""]');
        if (placeholder) placeholder.setAttribute('selected', 'selected');
      }

      setSessionSelectedState(picker, false);
      persistCalendarState(picker, { timeLabel: '' });
      disableAddToCart(picker);
      updateSessionNudges(picker);
      updateSessionSummary();
    }

    function refreshSessionAvailabilityFromSelect() {
      const select = getSessionSelect(picker);
      if (!select) return;
      sessions.forEach((session) => {
        const opt = Array.from(select.options).find(
          (option) =>
            option.dataset.dateValue === session.dateLabel && option.dataset.timeValue === session.timeLabel
        );
        if (opt) session.available = !opt.disabled;
      });
    }

    function renderTimes() {
      refreshSessionAvailabilityFromSelect();
      timeList.innerHTML = '';
      timesWrap.hidden = false;
      timesWrap.removeAttribute('hidden');

      if (!selectedDateKey) {
        timesWrap.classList.add('is-empty');
        timeList.innerHTML = '<p class="workshop-calendar__times-placeholder">Pick a date first</p>';
        updateSessionSummary();
        return;
      }

      const lampChecked = picker.querySelector(
        '[data-workshop-lamp-picker] input[type="radio"]:checked:not(.disabled)'
      );
      const lampValue = lampChecked?.value || null;

      const daySessions = (sessionsByDate[selectedDateKey] || []).filter((session) => {
        if (!session.available) return false;
        if (!lampValue) return true;
        return isComboAvailable(picker, session.dateLabel, session.timeLabel, lampValue);
      });

      if (!daySessions.length) {
        timesWrap.classList.add('is-empty');
        timeList.innerHTML =
          '<p class="workshop-calendar__times-placeholder">No times for this lamp on the selected date</p>';
        updateSessionSummary();
        return;
      }

      timesWrap.classList.remove('is-empty');
      daySessions
        .slice()
        .sort((a, b) => a.timeLabel.localeCompare(b.timeLabel))
        .forEach((session) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'workshop-calendar__time-btn';
          btn.textContent = formatTimeButtonLabel(selectedDateKey, session.timeLabel);
          btn.dataset.timeValue = session.timeLabel;
          btn.dataset.dateValue = session.dateLabel;
          if (selectedTime === session.timeLabel) btn.classList.add('is-selected');
          btn.addEventListener('click', () => {
            selectedTime = session.timeLabel;
            applySessionSelection(picker, session.dateLabel, session.timeLabel, {
              dateKey: selectedDateKey,
            });
            renderTimes();
          });
          timeList.appendChild(btn);
        });
      updateSessionSummary();
    }

    function renderMonth() {
      refreshSessionAvailabilityFromSelect();
      const current = months[monthIndex];
      monthLabel.textContent = monthName(current.year, current.month);

      const prevMonth = months[monthIndex - 1];
      const nextMonth = months[monthIndex + 1];
      prevBtn.disabled = !prevMonth;
      nextBtn.disabled = !nextMonth;
      if (prevLabel) prevLabel.textContent = prevMonth ? shortMonthName(prevMonth.year, prevMonth.month) : '';
      if (nextLabel) nextLabel.textContent = nextMonth ? shortMonthName(nextMonth.year, nextMonth.month) : '';
      prevBtn.setAttribute('aria-label', prevMonth ? `Previous month, ${monthName(prevMonth.year, prevMonth.month)}` : 'Previous month');
      nextBtn.setAttribute('aria-label', nextMonth ? `Next month, ${monthName(nextMonth.year, nextMonth.month)}` : 'Next month');

      grid.innerHTML = '';
      const firstDay = new Date(current.year, current.month, 1);
      // Monday-first: Mon=0 … Sun=6
      const startWeekday = (firstDay.getDay() + 6) % 7;
      const daysInMonth = new Date(current.year, current.month + 1, 0).getDate();

      for (let i = 0; i < startWeekday; i += 1) {
        const empty = document.createElement('span');
        empty.className = 'workshop-calendar__day is-outside';
        empty.setAttribute('aria-hidden', 'true');
        grid.appendChild(empty);
      }

      const lampCheckedForDays = picker.querySelector(
        '[data-workshop-lamp-picker] input[type="radio"]:checked:not(.disabled)'
      );
      const lampValueForDays = lampCheckedForDays?.value || null;

      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = new Date(current.year, current.month, day, 12, 0, 0, 0);
        const dateKey = toDateKey(date);
        const daySessions = sessionsByDate[dateKey] || [];
        const bookableSessions = daySessions.filter((session) => {
          if (!session.available) return false;
          if (!lampValueForDays) return true;
          return isComboAvailable(picker, session.dateLabel, session.timeLabel, lampValueForDays);
        });
        const available = bookableSessions.length > 0;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'workshop-calendar__day';
        btn.textContent = String(day);
        btn.setAttribute('aria-label', `${WEEKDAY_SHORT[date.getDay()]} ${day} ${MONTH_LONG[current.month]}`);

        if (available) {
          btn.classList.add('is-available');
          if (selectedDateKey === dateKey) btn.classList.add('is-selected');
          btn.addEventListener('click', () => {
            selectedDateKey = dateKey;
            selectedTime = null;

            const { dateFieldset } = getSessionFieldsets(picker);
            const dateLabel = bookableSessions[0]?.dateLabel || daySessions[0]?.dateLabel;
            const dateInput = findRadioByValue(dateFieldset, dateLabel);

            clearTimeSelection();
            if (dateInput) setRadioCheckedQuiet(picker, dateInput);
            persistCalendarState(picker, {
              dateKey,
              dateLabel: dateLabel || '',
              timeLabel: '',
            });

            renderMonth();
            renderTimes();
            updateSessionNudges(picker);
          });
        } else {
          btn.disabled = true;
        }

        grid.appendChild(btn);
      }
    }

    prevBtn.addEventListener('click', () => {
      if (monthIndex <= 0) return;
      monthIndex -= 1;
      renderMonth();
    });

    nextBtn.addEventListener('click', () => {
      if (monthIndex >= months.length - 1) return;
      monthIndex += 1;
      renderMonth();
    });
    root.dataset.calendarListeners = 'true';

    picker.__wsCalRefresh = () => {
      refreshSessionAvailabilityFromSelect();
      if (selectedDateKey && selectedTime) {
        const session = (sessionsByDate[selectedDateKey] || []).find(
          (item) => item.timeLabel === selectedTime && item.available
        );
        const lampChecked = picker.querySelector(
          '[data-workshop-lamp-picker] input[type="radio"]:checked:not(.disabled)'
        );
        const comboOk =
          session &&
          (!lampChecked || isComboAvailable(picker, session.dateLabel, session.timeLabel, lampChecked.value));
        if (!comboOk) {
          selectedTime = null;
          clearTimeSelection();
          if (session) {
            const { dateFieldset: df } = getSessionFieldsets(picker);
            const dateInput = findRadioByValue(df, session.dateLabel);
            if (dateInput) setRadioCheckedQuiet(picker, dateInput);
            persistCalendarState(picker, {
              dateKey: selectedDateKey,
              dateLabel: session.dateLabel,
              timeLabel: '',
            });
          }
        }
      }
      renderMonth();
      renderTimes();
      updateSessionNudges(picker);
    };

    try {
      restoreSelectionFromRadios();
      clearCalendarSkeleton(root);
      renderMonth();
      renderTimes();
      updateSessionNudges(picker);
      root.dataset.calendarInit = 'true';
    } catch (error) {
      console.error('[workshop-calendar] init failed', error);
      root.dataset.calendarInit = '';
      root.dataset.calendarListeners = '';
      renderCalendarSkeleton(root);
    }
  }

  function initPicker(picker) {
    if (!picker) return;

    const alreadyInited = picker.dataset.workshopPickerInit === 'true';
    if (!alreadyInited) {
      picker.dataset.workshopPickerInit = 'true';
      sortSessionOptions(picker);

      const persisted = readPersistedCalendarState(picker);
      const hasPersistedSession = !!(persisted.dateLabel || persisted.dateKey);

      if (!hasForcedPlaceholder) {
        // Don't wipe a session the shopper already chose (e.g. after Alpine remount).
        if (!(picker.dataset.workshopCalendarMode === 'true' && hasPersistedSession)) {
          clearDefaultSessionSelection(picker);
        }
        hasForcedPlaceholder = true;
      }

      const select = getSessionSelect(picker);
      if (select) {
        select.addEventListener('change', () => handleSessionChange(select));
      }

      syncSessionSelectFromRadios(picker);

      const lampFieldset = picker.querySelector('[data-workshop-lamp-picker]');
      if (lampFieldset) {
        lampFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', () => handleLampChange(input));
        });

        lampFieldset.querySelectorAll('label.workshop-lamp-card').forEach((label) => {
          label.addEventListener('mousedown', () => {
            const inputId = label.getAttribute('for');
            const input = inputId ? picker.querySelector(`#${escapeSelector(inputId)}`) : null;
            if (!input || input.disabled) return;

            const lampIndex = parseInt(lampFieldset.dataset.optionIndex, 10);
            const pendingSelections = getCurrentSelections(picker);
            pendingSelections[lampIndex] = input.value;
            updateLampMedia(picker, input, pendingSelections);
          });
        });
      }

      if (picker.dataset.workshopCalendarMode === 'true') {
        bindAtcGuards(picker);
      }
    }

    if (picker.dataset.workshopCalendarMode === 'true') {
      initCalendar(picker);
      updateSessionNudges(picker);
    }
  }

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll('.workshop-variant-picker').forEach(initPicker);
  }

  function recoverBrokenCalendars() {
    document.querySelectorAll('.workshop-variant-picker[data-workshop-calendar-mode="true"]').forEach((picker) => {
      const root = picker.querySelector('[data-workshop-calendar]');
      if (!root || !isCalendarRenderBroken(root)) return;
      root.dataset.calendarInit = '';
      root.dataset.calendarListeners = '';
      initCalendar(picker);
      updateSessionNudges(picker);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll();
      window.setTimeout(recoverBrokenCalendars, 0);
      window.setTimeout(recoverBrokenCalendars, 400);
    });
  } else {
    initAll();
    window.setTimeout(recoverBrokenCalendars, 0);
    window.setTimeout(recoverBrokenCalendars, 400);
  }

  document.addEventListener('shopify:section:load', (event) => {
    cachedProductVariants = null;
    hasForcedPlaceholder = false;
    initAll(event.target);
  });

  // Alpine replaces .variant-selects innerHTML on lamp change; re-bind calendar if needed.
  let remountRaf = 0;
  const remountObserver = new MutationObserver(() => {
    if (remountRaf) return;
    remountRaf = requestAnimationFrame(() => {
      remountRaf = 0;
      observeRoots();
      initAll();
    });
  });

  const observeRoots = () => {
    document.querySelectorAll('.variant-selects').forEach((host) => {
      if (host.dataset.wsCalObserve === 'true') return;
      host.dataset.wsCalObserve = 'true';
      // Alpine remounts replace direct children of .variant-selects; avoid subtree
      // so calendar grid/time rebuilds don't re-trigger initAll.
      remountObserver.observe(host, { childList: true, subtree: false });
    });
  };

  observeRoots();
  document.addEventListener('shopify:section:load', observeRoots);
  document.addEventListener('DOMContentLoaded', observeRoots);

  function syncAtc(root) {
    const scope = root || document;
    const pickers = scope.matches?.('.workshop-variant-picker')
      ? [scope]
      : Array.from(scope.querySelectorAll?.('.workshop-variant-picker') || []);
    pickers.forEach((picker) => {
      if (picker.dataset.workshopCalendarMode !== 'true') return;
      updateSessionNudges(picker);
    });
  }

  /**
   * Calendar mode: Alpine normally replaces .variant-selects innerHTML on every
   * option change, which remounts lamp cards and reloads images (visible flicker).
   * Sync availability / selection / prices from the fetched section instead.
   * @returns {boolean} true when surgical sync was applied (caller should skip remount)
   */
  function syncFromFetchedSection(liveRoot, fetchedDoc, syncGeneration) {
    if (!liveRoot || !fetchedDoc) return false;
    if (isStaleProductInfoSync(syncGeneration)) return true;

    const livePicker =
      (liveRoot.matches?.('.workshop-variant-picker[data-workshop-calendar-mode="true"]') && liveRoot) ||
      liveRoot.querySelector?.('.workshop-variant-picker[data-workshop-calendar-mode="true"]');
    if (!livePicker) return false;

    const fetchedMain = fetchedDoc.querySelector('.variant-selects-main') || fetchedDoc;
    const fetchedPicker = fetchedMain.querySelector?.(
      '.workshop-variant-picker[data-workshop-calendar-mode="true"]'
    );
    if (!fetchedPicker) return false;

    // Only apply selected-variant JSON when calendar memory still has a full session.
    // Otherwise a stale fetch can re-enable ATC for an old date×time.
    const persisted = readPersistedCalendarState(livePicker);
    const sessionComplete = !!(persisted.dateLabel && persisted.timeLabel);

    if (sessionComplete) {
      const liveVariantScript = livePicker.querySelector('script[data-selected-variant]');
      const fetchedVariantScript = fetchedPicker.querySelector('script[data-selected-variant]');
      if (liveVariantScript && fetchedVariantScript) {
        liveVariantScript.textContent = fetchedVariantScript.textContent;
      }
    }

    const sessionHost = livePicker.querySelector('[data-workshop-session-picker]');

    fetchedPicker.querySelectorAll('fieldset').forEach((fetchedFs) => {
      let liveFs = null;
      if (fetchedFs.id) liveFs = livePicker.querySelector(`#${escapeSelector(fetchedFs.id)}`);
      if (!liveFs && fetchedFs.dataset.optionIndex != null) {
        liveFs = livePicker.querySelector(`fieldset[data-option-index="${fetchedFs.dataset.optionIndex}"]`);
      }
      if (!liveFs) return;

      const isSessionFieldset = !!(sessionHost && sessionHost.contains(liveFs));
      const fetchedSelected = fetchedFs.querySelector(
        'input[type="radio"][data-value-selected="true"], input[type="radio"]:checked'
      );

      fetchedFs.querySelectorAll('input[type="radio"]').forEach((fetchedInput) => {
        const liveInput = liveFs.querySelector(
          `input[type="radio"][value="${escapeSelector(fetchedInput.value)}"]`
        );
        if (!liveInput) return;

        liveInput.disabled = fetchedInput.disabled;
        liveInput.classList.toggle('disabled', fetchedInput.classList.contains('disabled') || fetchedInput.disabled);
        if (fetchedInput.dataset.available != null) {
          liveInput.dataset.available = fetchedInput.dataset.available;
        }
        liveInput.dataset.valueSelected = fetchedInput.dataset.valueSelected || 'false';

        // Date/time checked state is owned by calendar memory — never trust stale fetch radios.
        if (!isSessionFieldset && fetchedSelected) {
          liveInput.checked = fetchedInput === fetchedSelected || fetchedInput.value === fetchedSelected.value;
        }

        const liveLabel = liveInput.id
          ? livePicker.querySelector(`label[for="${escapeSelector(liveInput.id)}"]`)
          : null;
        const fetchedLabel = fetchedInput.id
          ? fetchedPicker.querySelector(`label[for="${escapeSelector(fetchedInput.id)}"]`)
          : null;
        if (liveLabel && fetchedLabel) {
          liveLabel.classList.toggle('opacity-40', fetchedLabel.classList.contains('opacity-40'));
          const livePrice = liveLabel.querySelector('.workshop-lamp-card__price');
          const fetchedPrice = fetchedLabel.querySelector('.workshop-lamp-card__price');
          if (livePrice && fetchedPrice) livePrice.textContent = fetchedPrice.textContent;
        }
      });
    });

    const liveSelect = getSessionSelect(livePicker);
    const fetchedSelect = getSessionSelect(fetchedPicker);
    if (liveSelect && fetchedSelect) {
      Array.from(fetchedSelect.options).forEach((fetchedOpt) => {
        if (!fetchedOpt.value) return;
        const liveOpt = Array.from(liveSelect.options).find(
          (opt) =>
            opt.dataset.dateValue === fetchedOpt.dataset.dateValue &&
            opt.dataset.timeValue === fetchedOpt.dataset.timeValue
        );
        if (!liveOpt) return;
        liveOpt.disabled = fetchedOpt.disabled;
      });
    }

    reassertPersistedSessionRadios(livePicker);

    if (isStaleProductInfoSync(syncGeneration)) return true;

    if (typeof livePicker.__wsCalRefresh === 'function') {
      livePicker.__wsCalRefresh();
    } else {
      updateSessionNudges(livePicker);
    }

    return true;
  }

  function hasCalendarPicker(root) {
    if (!root) return false;
    if (root.matches?.('.workshop-variant-picker[data-workshop-calendar-mode="true"]')) return true;
    return !!root.querySelector?.('.workshop-variant-picker[data-workshop-calendar-mode="true"]');
  }

  window.xWorkshopVariantPicker = {
    initAll,
    triggerVariantUpdate,
    syncAtc,
    syncFromFetchedSection,
    hasCalendarPicker,
    beginProductInfoSync,
    cancelInFlightProductInfo,
    isStaleProductInfoSync,
  };
})();
